
import React, { useEffect, useState } from "react";
import { Grid, CircularProgress, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { callAPI, IsSmallScreen, Method, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import WSMobileDataGridAdvanced, { IOrderColumns } from "../../Components/DataGrid/WSMobileDataGridAdvanced";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import ConfirmDialog, { useConfirmDialog } from "../../Components/Confirmation/ConfirmDialog";
import PermissionDenied from "../../commonFunctions/PermissionDenied";
import { AllPermissions } from "../../commonFunctions/PermissionManger";

interface IVendorBill {
  billID: string;
  vendorID: string;
  vendorName?: string;
  billNo: string;
  billDate: string;
  dueDate: string;
  totalAmount: number;
  status: string;
}

interface IVendor {
  id: string;
  vendorName: string;
}

export const VendorBillList: React.FC<any> = React.memo(() => {
  const { add, edit, remove, view } = AllPermissions.getRolePermission;
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const isMobile = IsSmallScreen();
  const [bills, setBills] = useState<IVendorBill[]>([]);
  const [vendors, setVendors] = useState<IVendor[]>([]);
  const [loading, setLoading] = useState(false);

  const { open, title, onConfirm, handleClose, openConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    updateMainHeader("Vendor Bills");
    breadCrumbsStore.updateBreadCrumbs([{ "Vendor Bills": "" }]);
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch vendors first
      const vendorRes: any = await callAPI("Vendor", Method.GET);
      const vendorList: IVendor[] = (Array.isArray(vendorRes) ? vendorRes : vendorRes?.data || []).map((v: any) => ({
        id: v.id || v._id,
        vendorName: v.vendorName,
      }));
      setVendors(vendorList);

      // Fetch bills
      const billRes: any = await callAPI("VendorBill", Method.GET);
      const billList: any[] = Array.isArray(billRes)
        ? billRes
        : Array.isArray(billRes?.data)
        ? billRes.data
        : [];

      // Merge vendor name into each bill
      const mappedList: IVendorBill[] = billList.map((b: any) => {
        const vendor = vendorList.find(v => v.id === (b.vendorid || b.vendorID));
        return {
          billID: b.billID,
          vendorID: b.vendorid || b.vendorID || "",
          vendorName: vendor ? vendor.vendorName : "Unknown Vendor",
          billNo: b.billNo,
          billDate: b.billDate,
          dueDate: b.dueDate,
          totalAmount: b.totalAmount,
          status: b.status,
        };
      });

      setBills(mappedList);
    } catch (err) {
      console.error(err);
      showMessage("Error fetching Vendor Bills", "error");
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response: any = await callAPI(`VendorBill`, Method.DELETE, null, id);
      if (!response || response?.success === true) {
        showMessage("Deleted successfully", "success");
        fetchData();
      } else showMessage(response?.message || "Error deleting", "error");
    } catch (err) {
      console.error(err);
      showMessage("Error deleting", "error");
    } finally {
      handleClose();
    }
  };

  const handleAction = (action: string, row: IVendorBill) => {
    const id = row.billID;
    if (action === "Edit") navigate(`/vendorbill/edit/${id}`);
    else if (action === "Delete" && remove) openConfirmDialog("Delete this Vendor Bill?", () => handleDelete(id));
  };

  const columns: IColumns[] = [
    { header: "Bill No", fieldName: "billNo", type: "default" },
    { header: "Vendor Name", fieldName: "vendorName", type: "default" }, // ✅ show vendorName instead of ID
    { header: "Bill Date", fieldName: "billDate", type: "date" },
    { header: "Due Date", fieldName: "dueDate", type: "date" },
    { header: "Total Amount", fieldName: "totalAmount", type: "default" },
    { header: "Status", fieldName: "status", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
      actionCallback: handleAction,
    },
  ];

  const mobColumns: IOrderColumns[] = [
    { header: "Bill No", fieldName: "billNo", type: "default" },
    { header: "Vendor Name", fieldName: "vendorName", type: "default" },
    { header: "Total Amount", fieldName: "totalAmount", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
      actionCallback: handleAction,
    },
  ];

  const handleAdd = () => navigate("/vendorbill/add");

  return (
    <Grid container spacing={2}>
      {view ? (
        <Grid item xs={12}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
              <CircularProgress />
            </Box>
          ) : !isMobile ? (
            <WSDataGrid
              id="vendorBillGrid"
              title="Vendor Bills"
              columns={columns}
              source={{ filter: [], page: 10, pageSize: 1000, sort: [], url: "VendorBill/search" }}
              searchBox={{ fieldName: "billNo" }}
              addButton={add ? { label: "New", color: "secondary" } : undefined}
              onAddButtonClick={handleAdd}
            />
          ) : (
            <WSMobileDataGridAdvanced
              title="Vendor Bills"
              id="vendorBillGrid"
              columns={mobColumns}
              data={bills}
              addButton={add ? { label: "New", color: "secondary" } : undefined}
              onAddButtonClick={handleAdd}
            />
          )}
        </Grid>
      ) : (
        <PermissionDenied />
      )}
      <ConfirmDialog open={open} title={title} onConfirm={onConfirm} onClose={handleClose} />
    </Grid>
  );
});
